import styled, { keyframes } from "styled-components/macro";

const openButtonIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const FolderCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  padding: 8px;
  margin: -8px;
  user-select: none;

  &.active {
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23ACACACFF' stroke-width='2' stroke-dasharray='6%2c 8' stroke-dashoffset='6' stroke-linecap='square'/%3e%3c/svg%3e");
    border-radius: 8px;

    .shelf-book {
      opacity: 0.3;
    }

    .shelves-empty {
      opacity: 0;
    }

    .drag-to-folder {
      opacity: 1;
    }

    &.isOver:before {
      content: "";
      position: absolute;
      top: 10px;
      left: 10px;
      right: 10px;
      bottom: 10px;
      border: 4px solid var(--primary-cta-background);
      border-radius: 8px;
      pointer-events: none;
    }
  }

  > .folder {
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
    height: 100%;
    cursor: pointer;
    user-select: none;
    text-align: left;
    transition: opacity 150ms ease-in-out, scale 150ms ease-in-out;

    &.isDragging {
      opacity: 0.5;
      scale: 0.9;
      cursor: grabbing;
    }

    &:focus-visible:not(.isDragging) {
      outline: 2px solid var(--primary-cta);
      outline-offset: 2px;
    }

    .folder-tile {
      position: relative;
      flex: none;
      width: min(100%, 229px);
      aspect-ratio: 2 / 3;
      overflow: hidden;
      border-radius: 10px;
      background-color: var(--menu-color-fullscreen);
      box-shadow: var(--card-drop-shadow);
      transition: box-shadow 200ms ease-in-out;

      &::before {
        content: "";
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: color-mix(in srgb, var(--grey-0) 4%, transparent);
        opacity: 0;
        transition: opacity 200ms ease-in-out;
      }
    }

    &:hover:not(.isDragging) .folder-tile,
    &:focus-visible:not(.isDragging) .folder-tile {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 16px 30px -8px rgba(0, 0, 0, 0.3);

      &::before {
        opacity: 1;
      }
    }

    .shelves {
      position: absolute;
      inset: 5px;
      overflow: hidden;
      border-radius: 6px;
      background: linear-gradient(to bottom, color-mix(in srgb, var(--grey-3) 60%, transparent), color-mix(in srgb, var(--grey-3) 22%, transparent));
      box-shadow: inset 0 8px 14px -6px rgba(0, 0, 0, 0.4), inset 0 -3px 6px -3px rgba(0, 0, 0, 0.14), inset 7px 0 10px -8px rgba(0, 0, 0, 0.3), inset -7px 0 10px -8px rgba(0, 0, 0, 0.3);
    }

    .shelf {
      position: absolute;
      left: 0;
      right: 0;
      height: 46%;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 9px;
        z-index: 50;
        pointer-events: none;
        background: linear-gradient(to bottom, color-mix(in srgb, var(--menu-color-fullscreen) 80%, white) 0, color-mix(in srgb, var(--menu-color-fullscreen) 80%, white) 1.5px, var(--menu-color-fullscreen) 1.5px);
        box-shadow: 0 5px 7px -3px rgba(0, 0, 0, 0.3);
      }
    }

    .shelf-books {
      position: absolute;
      left: 5%;
      right: 5%;
      top: 0;
      bottom: 0;
    }

    .shelf-book {
      position: absolute;
      bottom: 9px;
      pointer-events: none;
      transition: opacity 150ms ease-in-out;

      &.active {
        z-index: 45 !important;
      }
    }

    .shelf-overflow {
      position: absolute;
      inset: 0;
      z-index: 4;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.42);
      color: #fff;
      font-family: "Inter";
      font-weight: 600;
      font-size: 26cqw;
      font-variant-numeric: tabular-nums;
      text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
    }

    .shelf-open {
      all: unset;
      box-sizing: border-box;
      position: absolute;
      right: 6px;
      bottom: 6px;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 6px;
      background: rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(6px);
      color: #fff;
      cursor: pointer;
      opacity: 0;
      pointer-events: none;

      &:hover,
      &:focus-visible {
        background: rgba(0, 0, 0, 0.45);
      }

      &:focus-visible {
        outline: 2px solid var(--primary-cta);
        outline-offset: 2px;
      }
    }

    .shelf-book.active .shelf-open {
      opacity: 1;
      pointer-events: auto;
      animation: ${openButtonIn} 150ms ease-in-out;
    }

    &.isDragging .shelf-open {
      display: none;
    }

    .shelf-hit {
      position: absolute;
      top: 0;
      bottom: 9px;
      z-index: 40;
    }

    .shelf-book-inner {
      position: relative;
      transition: transform 200ms ease-in-out;
    }

    .shelf-book-inner > .project-cover {
      box-shadow: 0 3px 10px -2px rgba(0, 0, 0, 0.26), -5px 0 9px -4px rgba(0, 0, 0, 0.3);

      &::after {
        background:
          linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent 8%),
          linear-gradient(to left, rgba(0, 0, 0, 0.14), transparent 6%),
          linear-gradient(135deg, rgba(255, 255, 255, 0.14), transparent 40%);
      }
    }

    .shelf-book.active .shelf-book-inner > .project-cover {
      box-shadow: 0 6px 14px -3px rgba(0, 0, 0, 0.3), -5px 0 9px -4px rgba(0, 0, 0, 0.3);
    }

    &:not(.isDragging) .shelf-book.active .shelf-book-inner {
      transform: translateY(-5%);
    }

    @media (prefers-reduced-motion: reduce) {
      .shelf-book-inner {
        transition: none;
      }
    }

    .shelves-empty {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: "Inter";
      font-weight: 500;
      font-size: 13px;
      color: var(--grey-2);
    }

    .drag-to-folder {
      position: absolute;
      top: 40%;
      left: 0;
      right: 0;
      z-index: 5;
      color: var(--grey-1);
      font-size: 16px;
      font-weight: 500;
      text-align: center;
      pointer-events: none;
      transition: opacity 150ms ease-in-out;
      opacity: 0;
    }

    .series-tag {
      position: absolute;
      left: 0;
      bottom: 0;
      z-index: 60;
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px 6px 10px;
      border-radius: 0 10px 0 0;
      background: var(--menu-color-fullscreen);
      box-shadow: 2px -1px 4px rgba(0, 0, 0, 0.1);
      color: var(--grey-1);
      font-family: "GT Super Text";
      font-size: 14px;
      font-variant: small-caps;
      pointer-events: none;

      svg {
        width: 16px;
        height: 16px;
      }

      svg path {
        fill: var(--grey-1);
      }
    }

    .folder-title {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 4px;
      width: min(100%, 229px);

      .action-menu {
        flex: none;
        opacity: 0;
        transition: opacity 150ms ease-in-out;

        .icon-wrapper,
        .icon-button-wrapper:hover .icon-wrapper,
        .icon-button-wrapper:focus-visible .icon-wrapper {
          width: 24px;
          height: 24px;
          border-radius: 6px;
        }

        .icon-button-wrapper:active .icon-wrapper {
          width: 22px;
          height: 22px;
        }

        svg circle {
          fill: var(--grey-0);
        }

        .icon-button-wrapper:hover .icon-wrapper,
        .icon-button-wrapper:focus-visible .icon-wrapper {
          background-color: color-mix(in srgb, var(--grey-0) 10%, transparent);
        }

        .icon-button-wrapper:active .icon-wrapper {
          background-color: color-mix(in srgb, var(--grey-0) 18%, transparent);
        }
      }
    }

    &:hover:not(.isDragging) .folder-title .action-menu,
    &:focus-within .folder-title .action-menu {
      opacity: 1;
    }

    @media (hover: none) {
      .folder-title .action-menu {
        opacity: 1;
      }
    }

    .folder-subtitle {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;
      margin-top: -8px;

      p {
        margin: 0;
        font-family: "Inter";
        font-weight: 500;
        font-size: 12px;
        color: var(--grey-0);
      }

      .folder-last-edited::before {
        content: "·";
        margin-right: 6px;
      }
    }
  }
`;
